// src/App.tsx

import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Categories } from './components/Categories';
import { ProductSection } from './components/ProductSection';
import { Features } from './components/Features';
import { Footer } from './components/Footer';
import { Cart } from './components/Cart';
import { Wishlist } from './components/Wishlist';
import { Shop } from './pages/Shop';
import { ProductDetails } from './pages/ProductDetails';
import { AboutUs } from './pages/AboutUs';
import { Contact } from './pages/Contact';
import { Checkout } from './pages/Checkout';
import { WhatsAppButton } from './components/WhatsAppButton';
import { Dashboard } from './admin/pages/Dashboard';
import { Products } from './admin/pages/Products';
import { Orders } from './admin/pages/Orders';
import { Customers } from './admin/pages/Customers';
import { Inventory } from './admin/pages/Inventory';
import { Messages } from './admin/pages/Messages';
import { Categories as AdminCategories } from './admin/pages/Categories';
import { Login } from './admin/pages/auth/Login';
import { Register } from './admin/pages/auth/Register';
import { useApp } from './context/AppContext';
import { Product } from './types';
import { OrderSuccess } from './pages/OrderSuccess';
import { OrderFailed } from './pages/OrderFailed';
import { IPN } from './pages/IPN';
import { ProtectedRoute } from './admin/components/ProtectedRoute';
import { AdminProvider } from './admin/context/AdminContext';
import { WelcomePopup } from './components/WelcomePopup';
import { CookieConsent } from './components/CookieConsent';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { FAQ } from './pages/FAQ';
import { CategoriesPage } from './pages/CategoriesPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import BottomNavigation from './components/BottomNavigation';

/**
 * The Home component used for the root "/" route.
 * It shows the Hero, Categories, some product sections, etc.
 */
const Home: React.FC = () => {
  const { state } = useApp();
  const navigate = useNavigate();

  // Handler for "Shop Now" button
  const handleShopNow = () => {
    navigate('/shop');
  };

  // Handler for category selection
  const handleCategorySelect = (categoryId: number) => {
    navigate(`/shop?category=${categoryId}`);
  };

  return (
    <>
      <Hero onShopNow={handleShopNow} />
      <Categories onCategorySelect={handleCategorySelect} />
      {/* Example sections with filtered products from state.products */}
      <ProductSection
        title="Featured Products"
        products={(state.products || []).filter(p => p.featured === 1 && p.active === 1).slice(0, 4)}
      />
      <ProductSection
        title="Best Sellers"
        products={(state.products || []).filter(p => p.best_seller === 1 && p.active === 1).slice(0, 4)}
      />
      <ProductSection
        title="New Arrivals"
        products={(state.products || []).filter(p => p.new_arrival === 1 && p.active === 1).slice(0, 4)}
      />
      <ProductSection
        title="Most Viewed"
        products={(state.products || []).filter(p => p.most_viewed === 1 && p.active === 1).slice(0, 4)}
      />
      <Features />
    </>
  );
};

function App() {
  const [showCart, setShowCart] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const { state, dispatch, isLoading, error } = useApp();
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null); // State to store the install prompt event
  const [showInstallBanner, setShowInstallBanner] = useState(false); // State to control banner visibility

  useEffect(() => {
    // Check when the user last visited
    const lastVisit = localStorage.getItem('lastVisit');
    const currentTime = new Date().getTime();
    
    // If no last visit or more than 6 hours have passed
    if (!lastVisit || currentTime - parseInt(lastVisit) > 6 * 60 * 60 * 1000) {
      setShowWelcomePopup(true);
      localStorage.setItem('lastVisit', currentTime.toString());
    }
  }, []);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    if (!cookiesAccepted) {
      setShowCookieConsent(true);
    }
  }, []);

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('beforeinstallprompt fired', e);
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      // Update UI to notify the user they can add to home screen
      // Check if the banner should be shown based on frequency
      const lastPromptTime = localStorage.getItem('lastInstallPromptTime');
      const currentTime = new Date().getTime();
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      if (!lastPromptTime || (currentTime - parseInt(lastPromptTime) > twentyFourHours)) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) {
      // App is installed, hide the banner
      setShowInstallBanner(false);
    }

    return () => {
      // Clean up the event listener
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Check if we're in an admin route
  const isAdminRoute = window.location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return (
      <ErrorBoundary>
        <Router future={{ v7_startTransition: true }}>
          <AdminProvider>
            <Routes>
              {/* Public admin routes */}
              <Route path="/admin/login" element={<Login />} />
              
              {/* Protected admin routes */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/products" element={
                <ProtectedRoute>
                  <Products />
                </ProtectedRoute>
              } />
              <Route path="/admin/orders" element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              } />
              <Route path="/admin/customers" element={
                <ProtectedRoute>
                  <Customers />
                </ProtectedRoute>
              } />
              <Route path="/admin/inventory" element={
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              } />
              <Route path="/admin/messages" element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              } />
              <Route path="/admin/categories" element={
                <ProtectedRoute>
                  <AdminCategories />
                </ProtectedRoute>
              } />
              <Route path="/admin/register" element={
                <ProtectedRoute>
                  <Register />
                </ProtectedRoute>
              } />
              
              {/* Redirect all other admin routes to login */}
              <Route path="/admin/*" element={<Navigate to="/admin/login" replace />} />
            </Routes>
          </AdminProvider>
        </Router>
      </ErrorBoundary>
    );
  }

  const handleCookieAccept = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setShowCookieConsent(false);
  };

  const handleCookieDecline = () => {
    localStorage.setItem('cookiesAccepted', 'false');
    setShowCookieConsent(false);
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white pb-16 sm:pb-0">
        <Router future={{ v7_startTransition: true }}>
          <Navbar
            onCartClick={() => setShowCart(!showCart)}
            onWishlistClick={() => setShowWishlist(!showWishlist)}
          />
          <main>
            {isLoading ? (
              <div className="min-h-[50vh] flex items-center justify-center">
                <LoadingSpinner size="large" />
              </div>
            ) : error ? (
              <div className="min-h-[50vh] flex items-center justify-center">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
            ) : (
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:slug" element={<ProductDetails />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/OrderSuccess" element={<OrderSuccess />} />
                <Route path="/OrderFailed" element={<OrderFailed />} />
                <Route path="/ipn" element={<IPN />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/faq" element={<FAQ />} />

                {/* Fallback 404 route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            )}
          </main>
          <Footer />
          {/* Add Bottom Navigation here, visible only in standalone mode */}
          {!isAdminRoute && (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) && <BottomNavigation />}

          {showCart && (
            <div>
              <Cart
                onCheckout={() => {
                  // navigate to /checkout
                  setShowCart(false);
                  window.location.href = '/checkout';
                }}
                onClose={() => setShowCart(false)}
              />
            </div>
          )}
          {showWishlist && (
            <div>
              <Wishlist onClose={() => setShowWishlist(false)} />
            </div>
          )}

          <WhatsAppButton />

          <WelcomePopup 
            isOpen={showWelcomePopup} 
            onClose={() => setShowWelcomePopup(false)} 
          />

          <CookieConsent 
            isOpen={showCookieConsent}
            onAccept={handleCookieAccept}
            onDecline={handleCookieDecline}
          />

          {/* PWA Install Banner */}
          {showInstallBanner && deferredPrompt && !isAdminRoute && (
            <div className="fixed bottom-16 left-0 right-0 bg-blue-600 text-white p-4 text-center z-50">
              <p>Add Shynerz Web App to your home screen for a better experience!</p>
              <button 
                className="mt-2 px-4 py-2 bg-white text-blue-600 rounded-md"
                onClick={() => {
                  // Hide the banner
                  setShowInstallBanner(false);
                  // Show the install prompt
                  deferredPrompt.prompt();
                  // Wait for the user to respond to the prompt
                  deferredPrompt.userChoice.then((choiceResult: any) => {
                    if (choiceResult.outcome === 'accepted') {
                      console.log('User accepted the A2HS prompt');
                    } else {
                      console.log('User dismissed the A2HS prompt');
                      // Record the time the user dismissed the prompt to manage frequency
                      localStorage.setItem('lastInstallPromptTime', new Date().getTime().toString());
                    }
                    // We no longer need the prompt. Clear it.
                    setDeferredPrompt(null);
                  });
                }}
              >
                Install App
              </button>
              <button 
                className="ml-4 mt-2 px-4 py-2 text-white border border-white rounded-md"
                onClick={() => {
                  setShowInstallBanner(false);
                  // Record the time the user dismissed the banner to manage frequency
                  localStorage.setItem('lastInstallPromptTime', new Date().getTime().toString());
                }}
              >
                Dismiss
              </button>
            </div>
          )}

        </Router>
      </div>
    </ErrorBoundary>
  );
}

export default App;
